import React, { useState } from 'react';
import { ChatComposerFocusGuard } from './ChatComposerFocusGuard';
import { Box, IconButton, InputBase, Tooltip } from '@mui/material';
import { North } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { Icon } from './Icon';

const useStyles = makeStyles()((theme) => ({
  inputWrapper: {
    position: 'relative',
    backgroundColor: theme.palette.warmSurface.main,
    backgroundImage:
      'linear-gradient(rgba(0, 0, 0, 0.03), rgba(0, 0, 0, 0.03))',
    border: '1px solid rgba(0,0,0,0.1)',
    borderRadius: 16,
    '&:focus-within': {
      backgroundColor: '#fff',
      backgroundImage: 'none',
      borderColor: '#0B99FF',
    },
  },
  panelInput: {
    backgroundColor: theme.palette.background.default,
    backgroundImage: 'none',
  },
  floatingInput: {
    pointerEvents: 'auto',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  },
  input: {
    padding: '10px 12px 30px 12px',
    fontSize: 13,
    letterSpacing: '0.52px',
    lineHeight: '17px',
    color: 'rgba(0,0,0,0.8)',
    width: '100%',
  },
  uploadButtonAnchor: {
    position: 'absolute',
    bottom: 6,
    left: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  uploadButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    margin: 0,
    padding: 0,
    appearance: 'none',
    border: 'none',
    borderRadius: 5,
    backgroundColor: 'transparent',
    color: 'rgba(0,0,0,0.55)',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.05)',
      color: 'rgba(0,0,0,0.85)',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.4,
    },
  },
  submitButton: {
    position: 'absolute',
    bottom: 6,
    right: 6,
    width: 18,
    height: 18,
    padding: 0,
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.8)',
    color: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.9)',
    },
    '&.Mui-disabled': {
      opacity: 0.2,
      backgroundColor: 'rgba(0,0,0,0.8)',
      color: '#fff',
    },
  },
}));

export type ChatComposerOpenAction = 'menu' | 'upload';

/** Shared message composer; callers supply attachments, menus and upload handling. */
export const ChatComposer: React.FC<
  React.PropsWithChildren<{
    value: string;
    onChange: (value: string) => void;
    onKeyDown: React.KeyboardEventHandler<
      HTMLInputElement | HTMLTextAreaElement
    >;
    onSend: () => void;
    onOpenConversation?: (action?: ChatComposerOpenAction) => void;
    canSend: boolean;
    disabled: boolean;
    placeholder: string;
    inputRef?: React.Ref<HTMLInputElement>;
    appearance?: 'panel' | 'floating';
    onUploadClick?: () => void;
    uploadDisabled?: boolean;
    uploadControl?: React.ReactNode;
    menu?: React.ReactNode;
  }>
> = ({
  value,
  onChange,
  onKeyDown,
  onSend,
  onOpenConversation,
  canSend,
  disabled,
  placeholder,
  inputRef,
  appearance = 'panel',
  onUploadClick,
  uploadDisabled,
  uploadControl,
  menu,
  children,
}) => {
  const { classes, cx } = useStyles();
  const [composer, setComposer] = useState<HTMLDivElement | null>(null);
  const [focused, setFocused] = useState(false);
  const openConversation = (target: EventTarget) => {
    const button = target instanceof Element ? target.closest('button') : null;
    const label = button?.getAttribute('aria-label');
    if (label === 'Chat menu') onOpenConversation?.('menu');
    else if (label === 'Upload files') onOpenConversation?.('upload');
    else onOpenConversation?.();
  };
  return (
    <Box
      data-chat-composer
      ref={setComposer}
      onFocusCapture={(event) => {
        if (event.currentTarget.contains(event.target)) {
          setFocused(true);
          onOpenConversation?.();
        }
      }}
      onClick={(event) => {
        if (
          event.target instanceof Node &&
          event.currentTarget.contains(event.target)
        )
          openConversation(event.target);
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setFocused(false);
      }}
      className={cx(
        classes.inputWrapper,
        classes.floatingInput,
        appearance === 'panel' && classes.panelInput,
      )}
    >
      <ChatComposerFocusGuard focused={focused} composer={composer} />
      <InputBase
        inputRef={inputRef}
        className={classes.input}
        multiline
        minRows={1}
        maxRows={6}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        inputProps={{ style: { resize: 'none' } }}
      />
      {uploadControl ?? (
        <span className={classes.uploadButtonAnchor}>
          <Tooltip title="Upload files" placement="top" disableInteractive>
            <span>
              <Box
                component="button"
                type="button"
                className={classes.uploadButton}
                aria-label="Upload files"
                disabled={uploadDisabled}
                onMouseDown={(event) => event.preventDefault()}
                onClick={onUploadClick}
              >
                <Icon category="mui" icon="Add" size={13} />
              </Box>
            </span>
          </Tooltip>
        </span>
      )}
      {children}
      {menu}
      <Tooltip title="Send" placement="top">
        <span>
          <IconButton
            className={classes.submitButton}
            size="small"
            aria-label="Send message"
            onClick={onSend}
            disabled={!canSend}
          >
            <North sx={{ fontSize: 12 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};
